import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "mb-l pb-xs border-gray-200 border-b-2 flex items-center justify-between" }
const _hoisted_2 = { class: "flex" }

import { computed, ref } from "vue";

import { ManagedPatient, ManagedPatientKey } from "@/interfaces/managedPatient";
import { supabase } from "@/helpers/supabase";
import { useSessionStore } from "@/store/sessionStore";
import { encryptSymmetric, hexToBytes } from "@/helpers/encryption";
import DialogCard from "@/components/DialogCard.vue";
import PatientFile from "./PatientFile.vue";
import UmanaHeadline from "@/components/UmanaHeadline.vue";
import IconButton from "@/components/IconButton.vue";
import ListTile from "@/components/ListTile.vue";
import { useNotificationStore } from "@/store/notificationStore";
import FileHistory from "./FileHistory.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'PatientFileManagement',
  props: {
    managedPatient: {},
    managedPatientKey: {}
  },
  setup(__props: any) {

const sessionStore = useSessionStore();
const managedPatient = computed(() => props.managedPatient);

const isAddingFile = ref<boolean>(false);
const props = __props;

const showHistoryOfUser = ref<ManagedPatient | null>(null);
const files = computed(() => {
  return managedPatient.value?.history.filter((h) => h.type == 3);
});

const otherFiles = computed(() => {
  return files.value.filter((file) => file.decrypted_data.category == 0);
});
const receiptFiles = computed(() => {
  return files.value.filter((file) => file.decrypted_data.category == 1);
});

// upload
const uploadModel = ref<any>({});

function selectedFile() {
  // await getting file
  setTimeout(() => {
    if (uploadModel.value.file && uploadModel.value.file.length > 0) {
      let file = uploadModel.value.file;
      if (!uploadModel.value.fileName || uploadModel.value.fileName == "")
        uploadModel.value.fileName = file[0].name.split(".")[0];
    }
  }, 0);
}

async function uploadFile() {
  let file: File | null = null;
  try {
    file = uploadModel.value.file[0].file as File;
  } catch (e) {
    console.error("FILE NOT FOUND");
    return;
  }

  if (
    !props.managedPatientKey.decrypted_symmetric_key ||
    !managedPatient.value?.id ||
    !sessionStore.selectedCareProviderGroup?.id ||
    !file
  )
    return;

  const buffer = await file.arrayBuffer();
  const fileAsArray = new Uint8Array(buffer);

  if (fileAsArray.byteLength > Math.pow(10, 6)) {
    useNotificationStore().notifications.push({
      msg: `Fehler - Datei zu gross. #f01`,
      type: "warning",
    });
    window._paq.push(["trackEvent", "FileUpload", "tooBig", undefined]);
    return;
  }
  const encryptedFileObj = await encryptSymmetric(
    fileAsArray,
    props.managedPatientKey.decrypted_symmetric_key
  );
  const randomFileName = `${Math.round(Math.random() * 1000000000)}.enc`;
  const encryptedName = await encryptSymmetric(
    JSON.stringify({
      fileName:
        uploadModel.value.fileName +
        "." +
        uploadModel.value.file[0].name.split(".").at(-1),
      category: uploadModel.value.category,
    }),
    props.managedPatientKey.decrypted_symmetric_key,
    hexToBytes(encryptedFileObj.nonce)
  );

  await supabase.storage
    .from("patient_files")
    .upload(
      `${managedPatient.value.id}/${randomFileName}`,
      hexToBytes(encryptedFileObj.encryptedMessage),
      {
        cacheControl: "3600",
        upsert: false,
      }
    );

  await supabase
    .from("managedPatient_data")
    .insert({
      managed_patient_id: managedPatient.value.id,
      used_key_id: props.managedPatientKey.key_id,
      nonce: encryptedFileObj.nonce,
      encrypted_data: encryptedName.encryptedMessage,
      owner_care_provider_group_id: sessionStore.selectedCareProviderGroup.id,
      type: 3,
      file_path: `${managedPatient.value.id}/${randomFileName}`,
    })
    .returns<ManagedPatient[]>();

  window._paq.push(["trackEvent", "FileUpload", "success", undefined]);
  isAddingFile.value = false;
}

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(UmanaHeadline, { h: "h2" }, {
        default: _withCtx(() => [
          _createTextVNode(" Dateien ")
        ]),
        _: 1
      }),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(IconButton, {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (showHistoryOfUser.value = managedPatient.value)),
          icon: "changeLog"
        }),
        _createVNode(IconButton, {
          onClick: _cache[1] || (_cache[1] = ($event: any) => (isAddingFile.value = true)),
          icon: "plus",
          class: "ml-xs"
        })
      ])
    ]),
    (receiptFiles.value.length > 0)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _createVNode(UmanaHeadline, { h: "h3" }, {
            default: _withCtx(() => [
              _createTextVNode("Rezepte")
            ]),
            _: 1
          }),
          _createElementVNode("ul", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(receiptFiles.value, (file) => {
              return (_openBlock(), _createBlock(ListTile, {
                key: file.id
              }, {
                main: _withCtx(() => [
                  _createVNode(PatientFile, {
                    "allow-delete": true,
                    file: file,
                    patient: managedPatient.value
                  }, null, 8, ["file", "patient"])
                ]),
                _: 2
              }, 1024))
            }), 128))
          ])
        ], 64))
      : _createCommentVNode("", true),
    (otherFiles.value.length > 0)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createVNode(UmanaHeadline, { h: "h3" }, {
            default: _withCtx(() => [
              _createTextVNode("Andere Dateien")
            ]),
            _: 1
          }),
          _createElementVNode("ul", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(otherFiles.value, (file) => {
              return (_openBlock(), _createBlock(ListTile, {
                key: file.id
              }, {
                main: _withCtx(() => [
                  _createVNode(PatientFile, {
                    "allow-delete": true,
                    file: file,
                    patient: managedPatient.value
                  }, null, 8, ["file", "patient"])
                ]),
                _: 2
              }, 1024))
            }), 128))
          ])
        ], 64))
      : _createCommentVNode("", true),
    _createVNode(DialogCard, {
      "is-visible": isAddingFile.value,
      onClose: _cache[3] || (_cache[3] = ($event: any) => (isAddingFile.value = false)),
      title: "Datei hochladen",
      "show-footer": false
    }, {
      default: _withCtx(() => [
        _createVNode(_component_FormKit, {
          type: "form",
          modelValue: uploadModel.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((uploadModel).value = $event)),
          class: "mt-5",
          onSubmit: uploadFile,
          "submit-label": "Datei verschlüsseln und hochladen"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_FormKit, {
              type: "file",
              onInput: selectedFile,
              name: "file",
              label: "Datei auswählen",
              validation: "required"
            }),
            _createVNode(_component_FormKit, {
              type: "text",
              name: "fileName",
              validation: "required",
              label: "Dateiname"
            }),
            _createVNode(_component_FormKit, {
              type: "multiselect",
              label: "Kategorie",
              name: "category",
              placeholder: "Kategorie wählen",
              "allow-empty": false,
              "select-options": [0, 1],
              "custom-label": 
            (i) =>
              [
                'Andere',
                'Rezept',
              ][i]
          ,
              validation: "required"
            }, null, 8, ["custom-label"])
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]),
      _: 1
    }, 8, ["is-visible"]),
    _createVNode(FileHistory, {
      "managed-patient": showHistoryOfUser.value,
      onClose: _cache[4] || (_cache[4] = ($event: any) => (showHistoryOfUser.value = null))
    }, null, 8, ["managed-patient"])
  ]))
}
}

})